import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';

import { logError } from 'lib/sentry/logError';
import { Box, Divider, Flex } from 'shared';

import { TextEditorFormat } from 'shared/components/TextEditor/types';

import { Placeholder } from './components/Placeholder';
import { namespace, TextEditorFormats } from './constants';
import { editorNodes } from './nodes/editorNodes';
import { BasicToolbarPlugin } from './plugins/BasicToolbarPlugin';
import { ShortcutPlugin } from './plugins/ShortcutPlugin';
import { TextEditorOnChangePlugin } from './plugins/TextEditorOnChangePlugin';
import './textEditorStyles.css';
import { theme } from './themes/textEditorTheme';
import { prepareEditorState } from './utils';

type BasicTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  format?: TextEditorFormat;
};

const basicTextEditorNamespace = 'hos-basic-text-editor';

export const BasicTextEditor = ({
  value,
  onChange,
  placeholder = '',
  format = TextEditorFormats.html,
}: BasicTextEditorProps) => {
  const initialConfig = {
    namespace,
    onError: (error: Error) => {
      logError(error);
    },
    editorState: prepareEditorState({
      value,
    }),
    theme,
    nodes: [...editorNodes],
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <HistoryPlugin />
      <Flex
        borderRadius="var(--border-radius)"
        border="var(--border-secondary)"
        flexDirection="column"
        h="100%"
      >
        <Box flexGrow={1} flexShrink={0}>
          <RichTextPlugin
            data-testid="sidebar-session-notes"
            placeholder={<Placeholder>{placeholder}</Placeholder>}
            contentEditable={
              <ContentEditable
                className={`${basicTextEditorNamespace}__content`}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </Box>
        <Divider />
        <BasicToolbarPlugin />
        <ShortcutPlugin />
      </Flex>
      <TextEditorOnChangePlugin onChange={onChange} format={format} />
    </LexicalComposer>
  );
};
